import Link from 'next/link';
import clsx from 'clsx';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import { useNewFeatureNotifications } from '@/utils/hooks/user/useNewFeatureNotifications';

// Components
import Icon from '@/components/base/Icon';
import Badge from '@/components/base/Badge';
import Tooltip from '@/components/base/Tooltip';
import NavTabs from '@/components/composite/NavTabs';

// Types
import type { NavTabProps } from '@/components/composite/NavTabs';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

export type NavTabsLinksTabType = Pick<NavTabProps<typeof Link>, 'name' | 'icon' | 'iconActive'> & {
    id: string;
    url: string;
    disabled?: boolean;
    tooltip?: React.ReactNode;
    isRestrictedByPlan?: boolean;
    socialProfileRestriction?: SocialProfileNetworkType[];
};

export type NavTabsLinksProps = {
    tabs: NavTabsLinksTabType[];
};

const NavTabLinks = ({ tabs }: NavTabsLinksProps) => {
    const { deleteAllQueries } = useRouterQuery();
    const currentUrlDeletedQueries = deleteAllQueries();
    const currentUrl = currentUrlDeletedQueries.split('#')[0];
    const { activeSocialProfile } = useActiveSocialProfile();
    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    return (
        <NavTabs>
            {tabs.map((tab) => {
                if (
                    'socialProfileRestriction' in tab &&
                    !tab.socialProfileRestriction.includes(activeSocialProfile.social_network_type)
                ) {
                    return null;
                }

                return (
                    <Tooltip
                        key={tab.id}
                        interactive
                        disabled={!tab?.disabled}
                        content={tab?.tooltip}>
                        <div>
                            <NavTabs.NavTab
                                as={Link}
                                isActive={currentUrl === tab.url}
                                href={tab.url}
                                className={clsx({
                                    'pointer-events-none cursor-not-allowed opacity-60':
                                        tab.disabled
                                })}
                                {...tab}>
                                {tab?.isRestrictedByPlan ? (
                                    <Icon icon="crown" className="text-icoBlue" />
                                ) : null}
                                {isNewFeatureOnPages([tab.url]) ? <Badge variant="new" /> : null}
                            </NavTabs.NavTab>
                        </div>
                    </Tooltip>
                );
            })}
        </NavTabs>
    );
};

export default NavTabLinks;
